import React, { useState } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";
import { Lock, Mail } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthModal({ open, onOpenChange, defaultMode = "signin" }) {
    const { setUser } = useAuth();
    const [mode, setMode] = useState(defaultMode); // "signin" | "signup"
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
    });

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url =
                mode === "signup"
                    ? `${API}/auth/signup`
                    : `${API}/auth/login`;
            const payload =
                mode === "signup"
                    ? {
                          name: form.name,
                          email: form.email,
                          phone: form.phone,
                          password: form.password,
                      }
                    : { email: form.email, password: form.password };
            const res = await axios.post(url, payload);
            const { user, session_token } = res.data;
            if (session_token) {
                localStorage.setItem("session_token", session_token);
            }
            setUser(user);
            toast.success(
                mode === "signup"
                    ? `Welcome, ${user.name.split(" ")[0]}! 🌴`
                    : `Welcome back, ${user.name.split(" ")[0]}!`
            );
            onOpenChange(false);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="auth-modal"
                className="max-w-md w-[calc(100vw-2rem)] bg-cream rounded-[24px] border-0 p-0 overflow-hidden"
            >
                <div className="bg-forest text-white p-6 sm:p-7 relative overflow-hidden">
                    <div className="absolute -right-4 -top-6 text-7xl opacity-15">
                        🌴
                    </div>
                    <DialogTitle className="font-serif font-black text-2xl">
                        {mode === "signup" ? "Create your account" : "Welcome back"}
                    </DialogTitle>
                    <DialogDescription className="text-mint text-sm mt-1">
                        Track your orders, save your address, re-order in one tap.
                    </DialogDescription>
                </div>
                <div className="p-6 sm:p-7 space-y-4">
                    <button
                        data-testid="auth-google-btn"
                        onClick={startGoogleSignIn}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-full bg-white border border-mint hover:border-forest text-navy font-bold transition-colors active:scale-[0.99]"
                    >
                        <GoogleG />
                        Continue with Google
                    </button>

                    <div className="flex items-center gap-3 text-xs text-muted2 uppercase tracking-widest">
                        <span className="flex-1 h-px bg-mint" />
                        or {mode === "signup" ? "sign up" : "sign in"} with email
                        <span className="flex-1 h-px bg-mint" />
                    </div>

                    <form onSubmit={handleEmailAuth} className="space-y-3">
                        {mode === "signup" && (
                            <>
                                <FieldInput
                                    testid="auth-name"
                                    placeholder="Full name"
                                    value={form.name}
                                    onChange={upd("name")}
                                    required
                                />
                                <FieldInput
                                    testid="auth-phone"
                                    type="tel"
                                    placeholder="Phone (617) 555-0143"
                                    value={form.phone}
                                    onChange={upd("phone")}
                                    required
                                />
                            </>
                        )}
                        <FieldInput
                            testid="auth-email"
                            type="email"
                            icon={<Mail size={16} />}
                            placeholder="Email"
                            value={form.email}
                            onChange={upd("email")}
                            required
                        />
                        <FieldInput
                            testid="auth-password"
                            type="password"
                            icon={<Lock size={16} />}
                            placeholder={
                                mode === "signup"
                                    ? "Password (min 8 chars)"
                                    : "Password"
                            }
                            value={form.password}
                            onChange={upd("password")}
                            required
                        />
                        <Button
                            data-testid="auth-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-full bg-gold hover:bg-goldDark text-navy font-bold py-5 disabled:opacity-60"
                        >
                            {loading
                                ? "Please wait…"
                                : mode === "signup"
                                  ? "Create account"
                                  : "Sign in"}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted2">
                        {mode === "signup" ? (
                            <>
                                Already have an account?{" "}
                                <button
                                    type="button"
                                    onClick={() => setMode("signin")}
                                    className="text-forest font-bold underline-offset-2 hover:underline"
                                    data-testid="auth-switch-to-signin"
                                >
                                    Sign in
                                </button>
                            </>
                        ) : (
                            <>
                                New here?{" "}
                                <button
                                    type="button"
                                    onClick={() => setMode("signup")}
                                    className="text-forest font-bold underline-offset-2 hover:underline"
                                    data-testid="auth-switch-to-signup"
                                >
                                    Create an account
                                </button>
                            </>
                        )}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function FieldInput({ testid, icon, ...props }) {
    return (
        <div className="relative">
            {icon && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2">
                    {icon}
                </span>
            )}
            <input
                data-testid={testid}
                {...props}
                className={`w-full ${icon ? "pl-9" : "pl-4"} pr-4 py-3 rounded-xl bg-white border border-mint focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition`}
            />
        </div>
    );
}

function GoogleG() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
            <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
            />
            <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
            />
            <path
                fill="#FBBC05"
                d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
            />
            <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
            />
        </svg>
    );
}
